import React, { useCallback, useEffect, useState } from "react";
import { ContentWrapper, ContinueButton, RateBox, Error } from "../styles";
import InputAndSelect from "../../../../components/common/InputAndSelect";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { getPayoutTransferFee } from "../../redux/slice";
import {
    clearUserData,
    savedCNYData,
    savedPayoutV2Data,
    setStep,
} from "../../redux/formDataSlice";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrencyAmount } from "../../../../utils/currency";
import { Button } from "../../../../components/common/Button";
import ErrorMessage from "../../../../components/ErrorMessage";
import { capitalizeCurrencyCode } from "../../../../utils/helpers";
import Loader from "../../../../components/common/Loader";
import { CHINA_PAYOUT_SOURCE_WALLET_KEY } from "../../const";
import { appRoutes } from "../../../../app/router/utils";
import DestinationCountry from "./DestinationCountry";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAG_KEYS } from "../../../../utils/featureFlags";

type AmountType = {
    amount?: number;
    handleNext?: () => void;
    currency?: string;
    totalAmount?: number;
    balanceError?: boolean;
    fees?: string;
    destinationCountry?: string;
};

const Amount = ({ handleNext }: AmountType) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const enableNgnChinaPayout = useFeatureFlagEnabled(
        FEATURE_FLAG_KEYS.enableNgnChinaPayout,
    );
    const enablePayToAsia = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.enablePayToAsia);
    

    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const { control, handleSubmit, setValue, formState: { errors, isValid }, } = useForm<AmountType>({defaultValues: getSavedData});

    const INT_FORM_DATA = useAppSelector(
        (state) => state.formData?.payoutv2Data || {},
    );

    const [localError, setLocalError] = useState(false);
    const [userData, setUserData] = useState<AmountType>(INT_FORM_DATA);
    const [showMinimumError, setShowMinimumError] = useState<boolean>();
    const [destinationConfirmed, setDestinationConfirmed] = useState(
        getSavedData?.destinationCountry === "Nigeria",
    );
    const { wallets, loading } = useAppSelector((state) => state?.wallets || {});

    const minValue = 100

    const CURRENT_WALLET = wallets?.find((wallet) => wallet.currency === "NGN")

    const handleDestinationContinue = useCallback(
        (destinationCountry: string) => {
            if (destinationCountry === "China") {
                localStorage.setItem(CHINA_PAYOUT_SOURCE_WALLET_KEY, "NGN");
                dispatch(clearUserData());
                dispatch(setStep(0));
                dispatch(savedCNYData({ sourceCurrency: "NGN" }));
                if (enablePayToAsia) {
                    return navigate(appRoutes.asiaPayout( "cny"), {
                        state: { sourceCurrency: "NGN" },
                    });
                } 
                navigate(appRoutes.cnyPayoutv2(), {
                    state: { sourceCurrency: "NGN" },
                });
                return;
            }

            dispatch(savedPayoutV2Data({ destinationCountry: "Nigeria" }));
            setDestinationConfirmed(true);
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        if (userData?.amount < minValue) {
            setShowMinimumError(true);
        } else if (userData.totalAmount && userData.totalAmount > CURRENT_WALLET?.balance) {
            setLocalError(true);
            setShowMinimumError(false);
        } else {
            setShowMinimumError(false);
            setLocalError(false);
        }
    }, [userData.amount, userData.totalAmount]);

    const handleDataUpdate = useCallback((field: keyof AmountType, value: any) => {
        setUserData((prev) => {
            const updatedUserData: AmountType = {
                ...prev,
                [field]: value,
            };

            const currentAmount = Number(updatedUserData.amount || 0);
            const currentFees = Number(updatedUserData.fees || 0);

            if (!isNaN(currentAmount) && !isNaN(currentFees)) {
                updatedUserData.totalAmount = currentAmount + currentFees;
            } else {
                updatedUserData.totalAmount = 0;
            }

            return updatedUserData;
        });
    }, []);

    useEffect(() => {
        if (!destinationConfirmed) return;

        (async () => {
            const resultAction = await dispatch(getPayoutTransferFee("NG"));
            if (getPayoutTransferFee.fulfilled.match(resultAction)) {
                const { fees } = resultAction.payload;
                handleDataUpdate("fees", fees);
            }
        })();
    }, [destinationConfirmed, dispatch, handleDataUpdate]);

    const handleNextStep = useCallback(
        (data: AmountType) => {
            const amount = Number(data.amount ?? userData.amount ?? 0);
            const fees = Number(userData.fees ?? 0);
            const combinedData = {
                ...userData,
                ...data,
                amount,
                fees,
                totalAmount: amount + fees,
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, userData, handleNext],
    );

    const filteredWallets = wallets as AmountType[];

    const uniqueCurrencies = Array.from(
        new Set(filteredWallets?.map((wallet) => wallet.currency)),
    );

    const options = uniqueCurrencies.map((currency) => ({
        label: currency,
        value: currency,
    }));

    const walletToDebit = options?.filter((option) => option.value === "NGN");

    const disableContinueButton = userData.balanceError || loading || !isValid || localError || showMinimumError

    if (enableNgnChinaPayout && !destinationConfirmed) {
        return <DestinationCountry onContinue={handleDestinationContinue} />;
    }

    return (
        <ContentWrapper>
            <Loader isLoading={loading} />

            <div>
                <Controller
                    name="amount"
                    rules={{ required: "Amount is required" }}
                    control={control}
                    render={({ field }) => (
                        <InputAndSelect
                            placeholder="Amount"
                            amountValue={field.value}
                            onAmountChange={(value) => {
                                field.onChange(value)
                                handleDataUpdate("amount", value)
                            }}
                            selectValue={"NGN"}
                            width="100%"
                            options={walletToDebit}
                        />
                    )}
                />
                {showMinimumError && (
                    <div className="min">
                        <ErrorMessage
                            message={`Minimum amount: ${capitalizeCurrencyCode("NGN")} ${new Intl.NumberFormat().format(
                                minValue
                            )}`}
                        />
                    </div>
                )}
            </div>
            {errors?.amount && <Error>{errors?.amount?.message}</Error>}

            {userData.amount && <section>
                <RateBox>
                    <span className="fee-box">
                        +
                        <span>
                            {formatCurrencyAmount("NGN", userData?.fees ?? 0, 2) ?? 0}
                        </span>
                    </span>
                    <span>Processing fee</span>
                </RateBox>
            </section>}

            {userData.amount && (
                <section>
                    <RateBox>
                        <span className="fee-box">
                            <span>
                                {formatCurrencyAmount("NGN", userData.totalAmount, 2) ?? 0}
                            </span>
                        </span>
                        <span>Total amount</span>
                    </RateBox>
                </section>
            )}

            {localError && (
                <div className="min">
                    <span>Exceeds your wallet balance of {formatCurrencyAmount("NGN", CURRENT_WALLET?.balance, 2) ?? 0}.</span>
                    <Link to="/wallets"> Top-up funds</Link>
                </div>
            )}

            <ContinueButton>
                <Button
                    label="Continue"
                    height="48px"
                    fontSize="14px"
                    disabled={disableContinueButton}
                    onClick={handleSubmit(handleNextStep)}
                />
            </ContinueButton>
        </ContentWrapper>
    )
}

export default Amount